/**
 * @fileoverview Settings → Share Hans: everything an instructor can hand to
 * somebody else, and what they get for it.
 *
 * Two rows that used to be three cards in three places on the page:
 *
 *   - **Refer a teacher** — the ordinary share (one free month of the top
 *     plan per signup, capped) and, for a Founding Ensemble member, the
 *     cohort's own reward (every five signups add a complimentary year). The
 *     two pay out differently and a member earns both from the same link, so
 *     they are two progress lines under ONE copy button rather than one bar
 *     that would have to misdescribe the other.
 *   - **Family seats** — the top plan's perk for the families a teacher
 *     introduces (I12/I13). Explained in full, and absent until the family
 *     plan is live.
 *
 * The whole section self-hides when there is nothing to share: the referral
 * program is an ops kill switch (`/referral/stats` 404s), and the seats 404
 * while parent accounts are off. An empty "Share Hans" card would be a
 * heading with nothing under it.
 */

import { useState } from "react";
import { Check } from "lucide-react";
import { toast } from "sonner";

import { FamilySeatsRow, useFamilySeats } from "@/components/FamilySeatsCard";
import FoundingMedal from "@/components/FoundingMedal";
import { SettingsRow, SettingsSection } from "@/components/settings/SettingsSection";
import { Button } from "@/components/studio/primitives";
import { tierDisplayName } from "@/lib/tiers";

function Progress({ value, max, tone = "neutral", testId }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="h-1.5 overflow-hidden rounded-full bg-[var(--studio-interactive)]" data-testid={testId}>
      <div
        className={`h-full rounded-full transition-all ${tone === "accent" ? "bg-[var(--accent-yellow)]" : "bg-[var(--text-tertiary)]"}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function ReferralRow({ stats, last }) {
  const [copied, setCopied] = useState(false);
  const founding = stats.founding;
  const capped = stats.referral_reward_months_granted >= stats.max_reward_months;
  // The plan is NAMED from the payload, not typed here: the reward grants a
  // specific tier and that tier has been renamed twice while its key stood
  // still, so a literal would drift silently. With no name on the payload the
  // offer still stands, it just doesn't claim which plan.
  const reward = stats.reward_tier ? ` of ${tierDisplayName(stats.reward_tier)}` : "";

  const copy = () => {
    navigator.clipboard.writeText(stats.share_url);
    setCopied(true);
    toast.success("Invite link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <SettingsRow
      testId="referral-row"
      title="Refer a teacher"
      desc={
        <>
          When a teacher signs up through your link, you get 1 free month{reward}.
          {founding
            ? ` Every ${founding.referrals_per_renewal} who join also add another complimentary year to your Founding Ensemble access.`
            : ""}
        </>
      }
      align="start"
      trailing={
        // The link still earns a founding member a year after the ordinary
        // reward is capped, so it stays; for everyone else a capped program
        // is stated rather than offered.
        capped && !founding ? (
          <span className="font-sans text-[13px] font-medium text-[var(--accent-yellow-text)]" data-testid="referral-capped">
            All {stats.max_reward_months} referral months earned
          </span>
        ) : (
          <Button onPress={copy} className="px-4 py-2 text-[13px]" icon={copied ? undefined : "copy"} data-testid="referral-copy-link">
            {copied ? (
              <span className="inline-flex items-center gap-1.5">
                <Check className="h-4 w-4" /> Copied
              </span>
            ) : (
              "Copy invite link"
            )}
          </Button>
        )
      }
      last={last}
    >
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <div className="mb-1.5 flex items-center justify-between font-mono text-[11px] text-[var(--text-tertiary)]">
            <span>
              {capped
                ? `All ${stats.max_reward_months} reward months earned`
                : `${stats.referral_reward_months_granted} of ${stats.max_reward_months} reward months`}
            </span>
            <span>
              {stats.referral_count} referral{stats.referral_count === 1 ? "" : "s"}
            </span>
          </div>
          <Progress value={stats.referral_reward_months_granted} max={stats.max_reward_months} />
        </div>
        {founding && (
          <div data-testid="founding-progress">
            <div className="mb-1.5 flex items-center justify-between font-mono text-[11px] text-[var(--text-tertiary)]">
              <span>
                {founding.referrals_into_cycle} of {founding.referrals_per_renewal} toward your next year
              </span>
              <span className="inline-flex items-center gap-1 text-[var(--accent-yellow-text)]">
                <FoundingMedal size={12} /> Founding
              </span>
            </div>
            <Progress value={founding.referrals_into_cycle} max={founding.referrals_per_renewal} tone="accent" />
            {founding.renewals_granted > 0 && (
              <p className="mt-1.5 font-sans text-[12px] text-[var(--accent-yellow-text)]">
                You&apos;ve earned {founding.renewals_granted} extra year{founding.renewals_granted === 1 ? "" : "s"} so far.
              </p>
            )}
          </div>
        )}
      </div>
    </SettingsRow>
  );
}

/**
 * @param {object} props
 * @param {object|null} props.referralStats The `/referral/stats` payload, or
 *   null when the program is off.
 */
export default function ShareSection({ referralStats }) {
  const family = useFamilySeats();
  const hasSeats = !!family.seats;
  if (!referralStats && !hasSeats) return null;

  return (
    <SettingsSection title="Share Hans" testId="share-section">
      {referralStats && <ReferralRow stats={referralStats} last={!hasSeats} />}
      {hasSeats && <FamilySeatsRow seats={family.seats} share={family.share} reload={family.reload} last />}
    </SettingsSection>
  );
}
